import {
  FloorplanRevision,
  GenerateLassieResponse,
  GenerateShuttleResponse,
  Segment,
  Rectangle,
  SingularStationArea,
  ManualArea,
  RackingArea,
  HikStorage,
  Lassie,
  LassieMini,
  ShuttleRack,
} from '@thive/fddm-types';

import { ObjectValues } from '@/modules/common/types/general';

export type TemplateGenerationResponse = GenerateLassieResponse | GenerateShuttleResponse;

/**
 * @description union type of all distinct areas in the FDDM
 */
export type Area =
  FloorplanRevision['design']['areas'][keyof FloorplanRevision['design']['areas']][number];

/**
 * @description union type of all distinct groups in the FDDM
 */
export type Template = Lassie | LassieMini | ShuttleRack | HikStorage;

export interface SegmentSpatial2D {
  start: Segment['start'];
  end: Segment['end'];
  leftSideWidth: Segment['leftSideWidth'];
  rightSideWidth: Segment['rightSideWidth'];
}

export type SpatialArea2D =
  | { id: string; type: 'rectangle'; data: Rectangle }
  | {
      id: string;
      type: 'segment';
      data: SegmentSpatial2D;
    };

export type SegmentSpatialArea = Extract<SpatialArea2D, { type: 'segment' }>;
export type RectangleSpatialArea = Extract<SpatialArea2D, { type: 'rectangle' }>;

const ConnectionRelation = {
  areaA_to_segmentB: 'areaA_to_segmentB',
  segmentA_to_areaB: 'segmentA_to_areaB',
  segmentA_to_segmentB: 'segmentA_to_segmentB',
  areaA_to_areaB: 'areaA_to_areaB',
} as const;
export type ConnectionRelationType = (typeof ConnectionRelation)[keyof typeof ConnectionRelation];

type ConnectionEndType = 'segment' | 'area';

/**
 * Orientation of a detected spatial relation between two areas/segments.
 * - co_flow / counter_flow / crossing: used for highway segment to highway segment
 * - long_side / short_side: used for highway segment to non-highway area
 * - long_side: placeholder for area to area relations (orientation is not used)
 */
export type ConnectionRelationOrientation =
  | 'co_flow'
  | 'counter_flow'
  | 'crossing'
  | 'long_side'
  | 'short_side';

export type HighwayConnectionSide = Extract<
  ConnectionRelationOrientation,
  'long_side' | 'short_side'
>;

export type HighwayToAreaRelationType = Extract<
  ConnectionRelationType,
  'areaA_to_segmentB' | 'segmentA_to_areaB'
>;

export interface ConnectionRelation {
  areaA: { id: string; type: ConnectionEndType };
  areaB: { id: string; type: ConnectionEndType };
  orientation: ConnectionRelationOrientation;
}

export type AreaToAreaDirection = 'A_TO_B' | 'B_TO_A';

export const Panel_Display_Mode = {
  NONE: 'none',
  MIX: 'mix',
  AREA: 'area',
  HIGHWAY: 'highway',
  SINGULAR_STATION: 'singular_station',
  STATIONARY: 'stationary',
  RACKING: 'racking',
  WALL: 'wall',
  MANUAL: 'manual',
  OBSTACLE: 'obstacle',
  OBSTACLES: 'obstacles',
  TEMPLATE: 'template',
  LOGICAL_SPACES: 'logical_spaces',
  LOGICAL_SPACE: 'logical_space',
  TEMPLATE_LASSIE: 'template_lassie',
  TEMPLATE_LASSIE_MINI: 'template_lassie_mini',
  TEMPLATE_SHUTTLE_RACK: 'template_shuttle_rack',
  TEMPLATE_HIK: 'template_hik',
  GROUP: 'Group',
  GROUPS: 'Groups',
} as const;
export type PanelDisplayMode = ObjectValues<typeof Panel_Display_Mode>;

export const Area_Direction = {
  DOWN: 'down',
  UP: 'up',
  LEFT: 'left',
  RIGHT: 'right',
} as const;
export type AreaDirection = ObjectValues<typeof Area_Direction>;

export type AreaLoadHandlingType =
  | SingularStationArea['loadHandlingType']
  | ManualArea['loadHandlingType']
  | RackingArea['rackingLoadHandlingType'];
